import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AudioLines,
  Hash,
  Mic,
  // PhoneIncoming,
  PhoneOutgoing,
  PlusCircle,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES, type UserRole } from '@/lib/constants/roles';
import { formatDate } from '@/lib/utils/date';
import {
  useCampaignAnalysis,
  useDashboardMain,
  useResellerUsers,
  useSuperAdminCampaigns,
} from '../api';
import type { CampaignAnalysisRequest } from '../types';

const ANALYSIS_COLORS = ['#10b981', '#3b82f6', '#f43f5e', '#f59e0b'];
const PILL_COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#f43f5e', '#10b981', '#6366f1', '#06b6d4'];

/* ------------------------- Expenditure bar chart ------------------------- */

export function ExpenditureChart() {
  const dash = useDashboardMain();
  const data = dash.data?.dbExpenditure ?? [];
  if (dash.isLoading) {
    return <Skeleton className="h-full w-full" />;
  }
  if (data.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-muted-foreground">
        No expenditure recorded in the last 7 days.
      </p>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
        <XAxis
          dataKey="campaignDate"
          fontSize={10}
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <YAxis fontSize={10} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
        />
        <Bar dataKey="expenditure" fill="#3b82f6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ---------------------- Weekly campaigns bar chart ---------------------- */

export function WeeklyCampaignsChart() {
  const dash = useDashboardMain();
  const data = dash.data?.dbWeeklyCampaigns ?? [];
  if (dash.isLoading) return <Skeleton className="h-full w-full" />;
  if (data.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-muted-foreground">
        No campaigns ran in the last 7 days.
      </p>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
        <XAxis
          dataKey="scheduleDate"
          fontSize={10}
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <YAxis
          fontSize={10}
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
        />
        <Bar dataKey="campaignCount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ------------------------ Campaign analysis (admin + user) ------------------------ */

const CAMPAIGN_TYPES = [
  { value: 'all', label: 'All types' },
  { value: '1', label: 'Voice' },
  { value: '2', label: 'Voice + DTMF' },
  { value: '3', label: 'Voice + DTMF + Patch' },
  { value: '6', label: 'Inbound' },
  { value: '7', label: 'TTS' },
  { value: '8', label: 'TTS + DTMF' },
  { value: '9', label: 'TTS + Patch' },
];

export function CampaignAnalysisWidget({ role }: { role: UserRole }) {
  const userId = useAuthStore((s) => s.userId);
  const today = useMemo(() => new Date(), []);
  const oneWeekAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  }, []);
  const [startDate, setStartDate] = useState(formatDate(oneWeekAgo));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [campaignType, setCampaignType] = useState('all');
  const [campaignName, setCampaignName] = useState('');
  const [username, setUsername] = useState('');

  const isAdmin =
    role === USER_ROLES.ADMIN ||
    role === USER_ROLES.S_ADMIN ||
    role === USER_ROLES.SUB_SUPER_ADMIN;
  const resellerUsers = useResellerUsers();

  const req: CampaignAnalysisRequest | null = userId
    ? {
        userId: Number(userId),
        startDate,
        endDate,
        campaignType: campaignType === 'all' ? '' : campaignType,
        campaignName,
        username: isAdmin ? username : '', 
      }
    : null;
  const analysis = useCampaignAnalysis(req);

  const totals = useMemo(() => {
    const rows = analysis.data ?? [];
    return rows.reduce(
      (acc, r) => {
        acc.answered += r.answered ?? 0;
        acc.dtmf += r.dtmf ?? 0;
        acc.nonAnswered += r.nonAnswered ?? 0;
        acc.expenditure += r.expenditure ?? 0;
        return acc;
      },
      { answered: 0, dtmf: 0, nonAnswered: 0, expenditure: 0 },
    );
  }, [analysis.data]);

  const pieData = useMemo(
    () => [
      { name: 'Answered', value: totals.answered },
      { name: 'DTMF', value: totals.dtmf },
      { name: 'Non-answered', value: totals.nonAnswered },
      { name: 'Spend', value: totals.expenditure },
    ],
    [totals],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Filters */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <div className="space-y-1">
          <Label className="text-[10px]">Start</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">End</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Type</Label>
          <Select value={campaignType} onValueChange={setCampaignType}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CAMPAIGN_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Campaign name</Label>
          <Input
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="(optional)"
            className="h-8 text-xs"
          />
        </div>
        {isAdmin && (
          <div className="space-y-1">
            <Label className="text-[10px]">User</Label>
            <Select
              // Radix forbids empty-string `<SelectItem value="">`, so we
              // route "all users" through a sentinel and translate at the
              // edge. The form-level value stays `''` for "all".
              value={username === '' ? '__all__' : username}
              onValueChange={(v) => setUsername(v === '__all__' ? '' : v)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All users</SelectItem>
                {(resellerUsers.data ?? []).map((u) => (
                  <SelectItem key={u.username} value={u.username}>
                    {u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Chart + table */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[1fr_220px]">
        <div className="min-h-0 overflow-auto rounded-md border border-border">
          {analysis.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (analysis.data ?? []).length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              No analysis rows for this filter.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left">Campaign</th>
                  <th className="px-2 py-1.5 text-left">Type</th>
                  <th className="px-2 py-1.5 text-right">Answered</th>
                  <th className="px-2 py-1.5 text-right">DTMF</th>
                  <th className="px-2 py-1.5 text-right">Non-ans</th>
                  <th className="px-2 py-1.5 text-right">Spend</th>
                </tr>
              </thead>
              <tbody>
                {(analysis.data ?? []).map((r, i) => (
                  <tr
                    key={`${r.campaignName}-${i}`}
                    className="border-t border-border"
                  >
                    <td className="px-2 py-1.5">{r.campaignName}</td>
                    <td className="px-2 py-1.5">{r.campaignType}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.answered}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.dtmf}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-rose-700">
                      {r.nonAnswered}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.expenditure}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="min-h-[180px]">
          {pieData.every((p) => p.value === 0) ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              No totals to chart.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  outerRadius={70}
                  innerRadius={36}
                  paddingAngle={2}
                >
                  {pieData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={ANALYSIS_COLORS[i % ANALYSIS_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Campaign totals pills ----------------------- */

const PILL_FIELDS: Array<{
  key: 'numbersUploaded' | 'numbersProcessed' | 'dndCount' | 'callsConnected' | 'retryCount';
  label: string;
}> = [
  { key: 'numbersUploaded', label: 'Total numbers' },
  { key: 'numbersProcessed', label: 'Dialed' },
  { key: 'dndCount', label: 'DND' },
  { key: 'callsConnected', label: 'Connected' },
  { key: 'retryCount', label: 'Retries' },
];

export function CampaignPillsWidget() {
  const q = useSuperAdminCampaigns();
  const totals = useMemo(() => {
    const rows = q.data ?? [];
    return rows.reduce(
      (acc, r) => {
        for (const f of PILL_FIELDS) acc[f.key] += Number(r[f.key] ?? 0);
        acc.totalCampaigns += 1;
        if (r.campaignStatus === 1) acc.running += 1;
        return acc;
      },
      {
        totalCampaigns: 0,
        running: 0,
        numbersUploaded: 0,
        numbersProcessed: 0,
        dndCount: 0,
        callsConnected: 0,
        retryCount: 0,
      },
    );
  }, [q.data]);

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
      <Pill
        label="Total campaigns"
        value={totals.totalCampaigns}
        color={PILL_COLORS[0]!}
      />
      <Pill label="Running" value={totals.running} color={PILL_COLORS[1]!} />
      {PILL_FIELDS.map((f, i) => (
        <Pill
          key={f.key}
          label={f.label}
          value={totals[f.key]}
          color={PILL_COLORS[(i + 2) % PILL_COLORS.length]!}
        />
      ))}
    </div>
  );
}

function Pill({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div
      className="flex flex-col rounded-md border border-border bg-card px-2.5 py-2 shadow-sm"
      style={{ borderTopWidth: 3, borderTopColor: color }}
    >
      <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="font-mono text-lg font-semibold tabular-nums text-foreground">
        {value.toLocaleString()}
      </span>
    </div>
  );
}

/* ------------------------ Quick actions ------------------------ */

export function QuickActionsWidget({ role }: { role: UserRole }) {
  const isAdmin = role === USER_ROLES.ADMIN || role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;

  const ACTIONS = [
    {
      to: '/add-voice-campaign',
      label: 'New voice campaign',
      icon: PhoneOutgoing,
    },
    // ...(!isAdmin && hasInbound
    //   ? [
    //       {
    //         to: '/add-inbound-campaign',
    //         label: 'New inbound campaign',
    //         icon: PhoneIncoming,
    //       },
    //     ]
    //   : []),
    { to: '/add-ivr', label: 'New custom IVR', icon: Hash },
    { to: '/add-tts-flow', label: 'New TTS flow', icon: Mic },
    { to: '/voice-files', label: 'Voice library', icon: AudioLines },
  ];
  if (isAdmin) {
    ACTIONS.push({
      to: '/all-voice-files',
      label: 'Moderation queue',
      icon: AudioLines,
    });
  }

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {ACTIONS.map((a) => (
        <Button
          key={a.to}
          asChild
          variant="outline"
          className="h-auto justify-start gap-2 px-2 py-2"
        >
          <Link to={a.to}>
            <span className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-primary">
              <a.icon className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 text-left text-xs font-medium">
              {a.label}
            </span>
            <PlusCircle className="h-3.5 w-3.5 text-muted-foreground" />
          </Link>
        </Button>
      ))}
    </div>
  );
}
